import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  projectAllScenarios,
  projectAnimal,
  projectionWarnings,
  resolveAssumptions,
  type ProjectionWarning,
} from '@dairy/domain';
import { deterministicId } from '@dairy/shared';
import { useSession, useSettings } from '@/auth/useSession';
import { DateField } from '@/components/DateField';
import { IntInput, LitersInput, MoneyInput } from '@/components/inputs';
import { errorMessage } from '@/components/errors';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Field,
  Input,
  PageHeader,
  Segmented,
  Select,
  Spinner,
  buttonVariants,
} from '@/components/ui';
import { db } from '@/db/db';
import { useToday } from '@/db/hooks';
import { useFormat } from '@/lib/format';
import { useSyncStatus } from '@/sync/client';
import { emptyAssumptions } from './assumption-form';
import { getProjectionDraft, setProjectionDraft } from './projection-draft';
import { useProjectionDefaults } from './useProjectionDefaults';
import {
  changeProjectionKind,
  newProjectionForm,
  parseProjectionForm,
  type ProjectionFormValues,
} from './projection-input-form';

export function NewProjectionPage() {
  const { t } = useTranslation();
  const f = useFormat();
  const navigate = useNavigate();
  const { profile } = useSession();
  const settings = useSettings();
  const today = useToday();
  const defaults = useProjectionDefaults();
  const sync = useSyncStatus();
  const recordId = deterministicId(`proj-assumptions:${profile!.tenantId}`);
  const loaded = useLiveQuery(
    async () => ({ record: await db.projectionAssumptionSets.get(recordId) }),
    [recordId],
  );
  const [step, setStep] = useState<'KIND' | 'DETAILS'>(() => (getProjectionDraft() ? 'DETAILS' : 'KIND'));
  const [form, setForm] = useState(() => getProjectionDraft()?.form ?? newProjectionForm(today));
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const resolution = resolveAssumptions(
    loaded?.record?.data ?? emptyAssumptions(),
    defaults.cached?.data.values ?? {},
    settings,
  );
  const parsed = parseProjectionForm(form, settings);
  const signature = JSON.stringify({
    input: parsed.input,
    assumptions: resolution.assumptions,
    settings,
    name: parsed.name,
  });
  const analysis = useMemo(
    () =>
      parsed.input && resolution.assumptions
        ? projectAnimal(parsed.input, resolution.assumptions, settings)
        : null,
    [signature],
  );
  const herdPeak = defaults.cached?.data.herdMaxPeakMl.value ?? null;
  const warnings: ProjectionWarning[] =
    analysis && parsed.input
      ? projectionWarnings(parsed.input, analysis, herdPeak)
      : form.kind === 'COW' && form.milking && !form.measuredByOwner
        ? ['SELLER_YIELD']
        : [];
  useEffect(() => {
    if (sync.lastSyncAt && defaults.online) void defaults.refresh();
  }, [sync.lastSyncAt, defaults.online, defaults.refresh]);
  const update = <K extends keyof ProjectionFormValues>(key: K, value: ProjectionFormValues[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
    setError(null);
  };
  const fieldError = (key: string) =>
    submitted && parsed.errors[key]
      ? t(`projectionForm.errors.${parsed.errors[key]}`, { months: settings.calfMaxAgeMonths })
      : undefined;
  const control = (
    key:
      | 'name'
      | 'breed'
      | 'parity'
      | 'dimNow'
      | 'litersNow'
      | 'lastPeak'
      | 'expectedPeak'
      | 'purchasePrice'
      | 'endValue'
      | 'monthsPregnant',
  ) => {
    const Component = ['purchasePrice', 'endValue'].includes(key)
      ? MoneyInput
      : ['litersNow', 'lastPeak', 'expectedPeak'].includes(key)
        ? LitersInput
        : ['parity', 'dimNow', 'monthsPregnant'].includes(key)
          ? IntInput
          : Input;
    return (
      <Field label={t(`projectionForm.fields.${key}`)} htmlFor={`projection-${key}`} error={fieldError(key)}>
        <Component
          id={`projection-${key}`}
          value={form[key]}
          onChange={(e) => update(key, e.target.value)}
          aria-invalid={!!fieldError(key)}
          maxLength={key === 'name' ? 80 : key === 'breed' ? 60 : 20}
        />
      </Field>
    );
  };
  const warningList = () => (
    <ul className="space-y-2">
      {warnings.map((warning) => (
        <li key={warning}>{t(`projectionForm.warnings.${warning}`)}</li>
      ))}
    </ul>
  );
  const calculate = (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setError(null);
    if (!parsed.input || !resolution.assumptions) return;
    try {
      setProjectionDraft({
        tenantId: profile!.tenantId,
        userId: profile!.userId,
        name: parsed.name,
        input: parsed.input,
        assumptions: resolution.assumptions,
        settings,
        herdMaxPeakMl: herdPeak,
        form,
        results: projectAllScenarios(parsed.input, resolution.assumptions, settings),
      });
      navigate('/projections/draft');
    } catch (e) {
      setError(e);
    }
  };
  if (!loaded) return <Spinner />;
  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <Link to="/projections" className="inline-block text-sm font-medium text-brand-800 underline">{t('projectionSaved.title')}</Link>
      <PageHeader
        title={t('projectionForm.title')}
        actions={
          <Link
            to="/projections/assumptions"
            className={buttonVariants({ variant: 'secondary', size: 'sm' })}
          >
            {t('projectionForm.assumptions')}
          </Link>
        }
      />
      <p className="text-sm font-medium text-stone-600">
        {t(step === 'KIND' ? 'projectionForm.stepKind' : 'projectionForm.stepDetails')}
      </p>
      {step === 'KIND' ? (
        <Card className="space-y-4">
          <h2 className="text-lg font-semibold">{t('projectionForm.chooseKind')}</h2>
          <p className="text-sm text-stone-600">{t('projectionForm.femaleOnly')}</p>
          <div
            role="radiogroup"
            aria-label={t('projectionForm.chooseKind')}
            className="grid gap-3 sm:grid-cols-3"
          >
            {(['COW', 'HEIFER', 'CALF'] as const).map((kind) => (
              <button
                key={kind}
                type="button"
                role="radio"
                aria-checked={form.kind === kind}
                onClick={() => {
                  setForm(changeProjectionKind(form, kind));
                  setSubmitted(false);
                }}
                className={`min-h-32 rounded-xl border p-4 text-left ${form.kind === kind ? 'border-brand-700 bg-brand-50 ring-1 ring-brand-700' : 'border-stone-300 bg-white'}`}
              >
                <span className="block font-bold">{t(`projectionForm.kinds.${kind}`)}</span>
                <span className="mt-2 block text-sm text-stone-600">
                  {t(`projectionForm.kindHints.${kind}`, { months: settings.calfMaxAgeMonths })}
                </span>
              </button>
            ))}
          </div>
          <Button className="w-full sm:w-auto" onClick={() => setStep('DETAILS')}>
            {t('projectionForm.continue')}
          </Button>
        </Card>
      ) : (
        <form onSubmit={calculate} noValidate className="space-y-5">
          <Card className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-lg font-semibold">{t(`projectionForm.kinds.${form.kind}`)}</h2>
              <Button variant="ghost" size="sm" onClick={() => setStep('KIND')}>
                {t('projectionForm.changeKind')}
              </Button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {control('name')}
              {control('breed')}
              <Field
                label={t('projectionForm.fields.startDate')}
                htmlFor="projection-startDate"
                error={fieldError('startDate')}
              >
                <DateField
                  id="projection-startDate"
                  value={form.startDate}
                  onChange={(date) => update('startDate', date)}
                  invalid={!!fieldError('startDate')}
                />
              </Field>
              <Field label={t('projectionForm.fields.horizonMonths')} htmlFor="projection-horizonMonths">
                <Select
                  id="projection-horizonMonths"
                  value={form.horizonMonths}
                  onChange={(e) => update('horizonMonths', Number(e.target.value) as 12 | 24 | 36)}
                >
                  {[12, 24, 36].map((months) => (
                    <option key={months} value={months}>
                      {t('projectionForm.horizon', { months })}
                    </option>
                  ))}
                </Select>
              </Field>
              {control('purchasePrice')}
              {control('endValue')}
            </div>
          </Card>
          <Card className="space-y-4">
            <h2 className="font-semibold">{t('projectionForm.birthTitle')}</h2>
            <Segmented
              value={form.birthMode}
              onChange={(mode) => update('birthMode', mode)}
              options={[
                { value: 'DATE', label: t('projectionForm.birthDate') },
                { value: 'AGE', label: t('projectionForm.estimatedAge') },
              ]}
            />
            {form.birthMode === 'DATE' ? (
              <>
                <Field
                  label={t('projectionForm.birthDate')}
                  htmlFor="projection-birthDate"
                  error={fieldError('birthDate')}
                >
                  <DateField
                    id="projection-birthDate"
                    value={form.birthDate}
                    onChange={(date) => update('birthDate', date)}
                    max={form.startDate || undefined}
                    invalid={!!fieldError('birthDate')}
                  />
                </Field>
                <Checkbox
                  label={t('projectionForm.birthEstimated')}
                  checked={form.birthDateEstimated}
                  onChange={(e) => update('birthDateEstimated', e.target.checked)}
                />
              </>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Field htmlFor="projection-ageYears" label={t('projectionForm.years')}>
                    <IntInput
                      id="projection-ageYears"
                      value={form.ageYears}
                      onChange={(e) => update('ageYears', e.target.value)}
                    />
                  </Field>
                  <Field htmlFor="projection-ageMonths" label={t('projectionForm.months')}>
                    <IntInput
                      id="projection-ageMonths"
                      value={form.ageMonths}
                      onChange={(e) => update('ageMonths', e.target.value)}
                    />
                  </Field>
                </div>
                <p className="text-sm text-stone-600">{t('projectionForm.ageHint')}</p>
                {fieldError('age') ? (
                  <p role="alert" className="text-sm text-red-700">
                    {fieldError('age')}
                  </p>
                ) : null}
              </>
            )}
            {fieldError('kind') ? (
              <p role="alert" className="text-red-700">
                {fieldError('kind')}
              </p>
            ) : null}
          </Card>
          {form.kind === 'COW' ? (
            <Card className="space-y-4">
              <h2 className="font-semibold">{t('projectionForm.milkTitle')}</h2>
              {control('parity')}
              <Checkbox
                label={t('projectionForm.milking')}
                checked={form.milking}
                onChange={(e) => update('milking', e.target.checked)}
              />
              {form.milking ? (
                <>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {control('litersNow')}
                    {control('dimNow')}
                  </div>
                  <Checkbox
                    label={t('projectionForm.measured')}
                    checked={form.measuredByOwner}
                    onChange={(e) => update('measuredByOwner', e.target.checked)}
                  />
                </>
              ) : (
                control('lastPeak')
              )}
            </Card>
          ) : (
            <Card className="space-y-3">
              {control('expectedPeak')}
              <p className="text-sm text-stone-600">{t('projectionForm.peakFallback')}</p>
            </Card>
          )}
          {form.kind !== 'CALF' ? (
            <Card className="space-y-4">
              <h2 className="font-semibold">{t('projectionForm.pregnancyTitle')}</h2>
              <Checkbox
                label={t('projectionForm.pregnant')}
                checked={form.pregnant}
                onChange={(e) => update('pregnant', e.target.checked)}
              />
              {form.pregnant ? (
                <>
                  <Segmented
                    value={form.pregnancyMode}
                    onChange={(mode) => update('pregnancyMode', mode)}
                    options={[
                      { value: 'DATE', label: t('projectionForm.serviceDate') },
                      { value: 'MONTHS', label: t('projectionForm.monthsPregnant') },
                    ]}
                  />
                  {form.pregnancyMode === 'DATE' ? (
                    <Field
                      htmlFor="projection-serviceDate"
                      label={t('projectionForm.serviceDate')}
                      error={fieldError('serviceDate')}
                    >
                      <DateField
                        id="projection-serviceDate"
                        value={form.serviceDate}
                        onChange={(date) => update('serviceDate', date)}
                        max={form.startDate || undefined}
                        invalid={!!fieldError('serviceDate')}
                      />
                    </Field>
                  ) : (
                    <>
                      {control('monthsPregnant')}
                      {fieldError('serviceDate') ? (
                        <p role="alert" className="text-red-700">
                          {fieldError('serviceDate')}
                        </p>
                      ) : null}
                      <p className="text-sm text-stone-600">{t('projectionForm.pregnancyHint')}</p>
                    </>
                  )}
                </>
              ) : null}
            </Card>
          ) : null}
          {warnings.length ? (
            <Card className="space-y-2 border-amber-200 bg-amber-50 text-amber-950">
              <h2 className="font-semibold">{t('projectionForm.checkNumbers')}</h2>
              {warningList()}
            </Card>
          ) : null}
          {analysis ? (
            <p className="text-sm font-medium text-stone-700">
              {t('projectionForm.impliedPeak', { peak: f.liters(analysis.impliedPeakMl) })}
            </p>
          ) : null}
          <Card className="space-y-3">
            {resolution.missing.length ? (
              <>
                <Badge tone="amber">
                  {t('projections.missingCount', { count: resolution.missing.length })}
                </Badge>
                <p className="font-semibold">{t('projectionForm.setAssumptions')}</p>
                <ul className="list-inside list-disc text-sm text-stone-600">
                  {resolution.missing.map((key) => (
                    <li key={key}>{t(`projections.fields.${key}`)}</li>
                  ))}
                </ul>
                <Link
                  className="inline-block font-medium text-brand-800 underline"
                  to="/projections/assumptions"
                >
                  {t('projectionForm.assumptions')}
                </Link>
              </>
            ) : (
              <p className="text-sm font-medium text-brand-900">{t('projections.ready')}</p>
            )}
            {defaults.cached ? (
              <p className="text-sm text-stone-500">
                {t('projections.refreshed', {
                  date: f.date(defaults.cached.data.asOf),
                  time: f.time(defaults.cached.fetchedAt),
                })}
              </p>
            ) : null}
            {defaults.cached && !defaults.online ? (
              <p className="text-sm text-amber-900">{t('projections.cachedHint')}</p>
            ) : null}
            {defaults.error ? (
              <p className="text-sm text-red-700">{errorMessage(t, defaults.error)}</p>
            ) : null}
            {error ? (
              <p role="alert" className="text-red-700">
                {errorMessage(t, error)}
              </p>
            ) : null}
            {submitted && !parsed.input ? (
              <p role="alert" className="text-red-700">
                {t('projectionForm.correctFields')}
              </p>
            ) : null}
            <Button
              type="submit"
              size="lg"
              className="w-full sm:w-auto"
              disabled={resolution.missing.length > 0}
            >
              {t('projectionForm.calculate')}
            </Button>
          </Card>
        </form>
      )}
    </div>
  );
}
